import { Grid } from '@mui/material';
import classnames from 'classnames';
import { find, trim } from 'lodash';
import * as React from 'react';
import {
  ForwardedRef,
  ReactElement,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import Select, { ActionMeta, components } from 'react-select';
import ISelect from 'react-select/dist/declarations/src/Select';
import { SelectComponents } from 'react-select/dist/declarations/src/components';
import {
  SearchFilterOps,
  SearchFilterTypeAliases,
  SearchFilterViews,
  SearchFilterViewsType,
} from '../../constants/visibilityFilters';
import { useMacroOptionsContext } from '../../contexts/MacroOptionsContext';
import { useConst } from '../../hooks/useConst';
import { useScope } from '../../hooks/useScope';
import DataSourceIconCmp from '../data_sources/DataSourceIcon';
import DataSetIconCmp from '../sidebar/icons/DataSetIcon';
import './MacroAutocomplete.css';
import {
  MacroOption,
  getDefaultOptions,
  getResourceOptions,
  getStaticOptions,
} from './MacroAutocomplete.data';
import { MacroAutocompleteGroupHeading } from './MacroAutocompleteGroupHeading';
import { MacroAutocompleteInput } from './MacroAutocompleteInput';
import { MacroInputApi } from './MacroInput';
import { createMacro } from './tools/createMacro';
import { ignoreKeys } from './tools/ignoreKeys';
import { AvailableFilters } from './types/AvailableFilters';
import { ComplexValue } from './types/ComplexValue';
import { MacroParts } from './types/MacroParts';

import baseStyles from './MacroAutocomplete.styles';

// TODO: fix when components are in TS
const DataSourceIcon = DataSourceIconCmp as any;
const DataSetIcon = DataSetIconCmp as any;

export interface MacroAutocompleteProps {
  value: SearchFilterViewsType;
  requestTypes: string[];
  isOpen?: boolean | undefined;
  before?: () => ReactElement;
  after?: () => ReactElement;
  onOpen?: () => void;
  onClose?: (value: ComplexValue) => void;
  onChange?: (value: ComplexValue) => void;
  onSelect?: (option: MacroOption) => void;
  renderMenu?: () => ReactElement;
  view?: string;
  placeholder?: string;
  availableFilters?: AvailableFilters;
  resourceSuggestions?: Record<string, any>;
  getSourceMeta?: (id: string) => void;
  /** When true, render menu in a portal to fix z-index stacking inside modals */
  menuInPortal?: boolean;
}

const MacroAutocompleteFn = (
  props: MacroAutocompleteProps,
  forwardedRef: ForwardedRef<MacroInputApi>,
) => {
  const { availableFilters, resourceSuggestions, getSourceMeta, requestTypes } = props;
  const { update } = useMacroOptionsContext();

  const inputRef = useRef<MacroInputApi>();
  const selectRef = useRef<ISelect<any, false, any>>();
  // Use ref to avoid stale closure in useConst customComponents
  const availableFiltersRef = useRef(availableFilters);
  availableFiltersRef.current = availableFilters;

  useImperativeHandle(forwardedRef, () => inputRef.current);

  const callbacks = useScope({
    onOpen: props.onOpen,
    onClose: props.onClose,
    onChange: props.onChange,
    onSelect: props.onSelect,
  });

  const state = useConst(() => {
    const stateObj = {
      isFocused: false,
      isOpen: false,

      setFocused: () => (stateObj.isFocused = true),
      clearFocused: () => (stateObj.isFocused = false),

      setOpen: () => {
        callbacks.onOpen?.();
        stateObj.isOpen = true;
      },
      clearOpen: () => {
        const complex = inputRef.current?.getCurrentComplex();
        if (complex) {
          callbacks.onClose?.(complex);
        }
        stateObj.isOpen = false;
        update((current) => ({
          ...current,
          isOpen: false,
        }));
      },
    };
    return stateObj;
  });

  const scope = useScope({
    renderMenu: props.renderMenu,
    before: props.before,
    after: props.after,
    view: props.view,
    placeholder: props.placeholder,
  });

  const isLoading = useSelector((state) => requestTypes.some((type) => state['request'][type]));

  const { lastUpdated } = useSelector(
    (state) => state['app'].visibilityFilters.resourceSuggestions,
  );

  const customComponents = useConst<Partial<SelectComponents<any, false, any>>>(() => ({
    SelectContainer: (api) => {
      const { innerProps } = api;
      const { onKeyDown, onKeyUp } = innerProps;

      const updatedProps = {
        ...innerProps,
        onKeyDown: (e) => ignoreKeys(e, onKeyDown),
        onKeyUp: (e) => ignoreKeys(e, onKeyUp),
      };

      const newProps = { ...api, innerProps: updatedProps };
      return <components.SelectContainer {...newProps}>{api.children}</components.SelectContainer>;
    },
    Control: (api) => {
      const focused = state.isFocused || state.isOpen;
      const className = focused ? 'is-focused' : '';
      const classNames = ['macro-autocomplete-control', api.className || '', className];

      return (
        <components.Control {...api} className={classNames.join(' ')}>
          {scope.before?.()}
          {api.children}
          {scope.after?.()}
        </components.Control>
      );
    },
    Input: (api) => (
      <MacroAutocompleteInput
        ref={inputRef}
        api={api}
        view={scope.view}
        placeholder={scope.placeholder}
        onChange={callbacks.onChange}
        onFocus={api.onFocus}
        onBlur={api.onBlur}
      />
    ),
    Option: (api: any) => {
      const { key, meta, isResourceMatch } = api.data as any;
      const label = api.label as string;

      // don't render the top invisible option
      if (!label) {
        return null;
      }

      let token: string;
      let value: string | undefined;
      let description: ReactElement;

      const macro = api.data.macro as MacroParts;

      let content: ReactElement;
      let iconElement: ReactElement | null = null;

      if (isResourceMatch) {
        switch (scope.view) {
          case SearchFilterViews.AVAILABLE_DATA_SETS:
            iconElement = (
              <div className="entity-icon">
                <DataSetIcon className="data-set-icon" />
              </div>
            );
            break;

          case SearchFilterViews.MY_DATA_SETS:
            iconElement = (
              <div className="entity-icon">
                <DataSetIcon className="data-set-icon" />
                <span className="data-set-text">from</span>
                {meta ? (
                  <DataSourceIcon
                    className="data-source-icon"
                    icon={meta.iconSm}
                    name={meta.displayName}
                    displayName={false}
                  />
                ) : (
                  <DataSetIcon className="data-set-icon" />
                )}
              </div>
            );
            break;

          default:
            if (meta) {
              iconElement = (
                <div className="entity-icon">
                  <DataSourceIcon
                    className="data-source-icon"
                    icon={meta.iconSm}
                    name={meta.displayName}
                    displayName={false}
                  />
                </div>
              );
            }
        }

        content = (
          <div
            className="resource-suggestion-option"
            style={{ display: 'flex', alignItems: 'center' }}
          >
            {iconElement}
            <b>{label}</b>
          </div>
        );
      } else if (macro) {
        const filter = availableFiltersRef.current?.[macro.token];
        token = macro.token;
        value = trim(macro.value);
        const alias = SearchFilterTypeAliases[token];

        if (value && filter && (!macro.operator || SearchFilterOps[macro.operator])) {
          const { label: filterLabel, availableOpTypes, availableValues } = filter;
          const operator = SearchFilterOps[macro.operator || availableOpTypes[0]].label;
          let valueLabel = value;

          if (availableValues) {
            const availableValue = find(availableValues, {
              value: value,
            });

            if (availableValue) {
              valueLabel = availableValue.label;
            }
          }

          description = (
            <>
              {filterLabel} {operator} <b>{valueLabel}</b>
            </>
          );
        } else if (filter) {
          description = <>{filter.description || filter.label}</>;
        }

        content = (
          <Grid container spacing={1}>
            <Grid
              item
              xs={4}
              style={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <div className="macro-input-group ellipsis">
                <span
                  className={classnames('macro-input-token', {
                    'no-value': !value,
                  })}
                >
                  {token}
                </span>
                {value && (
                  <>
                    <span className="macro-input-operator">:</span>
                    <span className="macro-input-value">{value}</span>
                  </>
                )}
              </div>
            </Grid>
            <Grid item xs={8}>
              <div className="macro-input-option-description">
                {description || ''}
                {alias && !value && (
                  <>
                    {' '}
                    (alias of{' '}
                    <span className="macro-input-group ellipsis">
                      <span className="macro-input-token no-value">{alias}</span>
                    </span>
                    )
                  </>
                )}
              </div>
            </Grid>
          </Grid>
        );
      } else if (key === 'show-all-results') {
        content = <span className="macro-autocomplete-show-all-results-option">{label}</span>;
      } else {
        content = <>{label}</>;
      }

      return (
        <components.Option
          {...api}
          getStyles={(propertyName, _api) => {
            const styles = api.getStyles(propertyName, _api);
            return { ...styles, cursor: 'pointer' };
          }}
        >
          {content}
        </components.Option>
      );
    },
    IndicatorsContainer: () => null,
    Group: (api) => (
      <components.Group
        {...api}
        getStyles={(propertyName, _api) => {
          const styles = api.getStyles(propertyName, _api);
          return { ...styles, paddingTop: 0 };
        }}
      >
        {api.children}
      </components.Group>
    ),
    GroupHeading: MacroAutocompleteGroupHeading,
    Placeholder: () => null,
    SingleValue: () => null,
    Menu: (api) => {
      // if the only option is the top invisible option, render nothing
      return api.options.length <= 1 ? null : <components.Menu {...api} />;
    },
    MenuList: (api) => {
      const customMenu = scope.renderMenu?.();

      return (
        <components.MenuList
          {...api}
          getStyles={(propertyName, _api) => {
            const styles = api.getStyles(propertyName, _api);
            return customMenu
              ? {
                  ...styles,
                  padding: 0,
                }
              : styles;
          }}
        >
          {customMenu || api.children}
        </components.MenuList>
      );
    },
  }));

  useEffect(() => {
    const input = inputRef.current;
    if (input) input.setValue(props.value);
  }, [trim(props.value)]);

  const onSelect = useCallback((option: MacroOption, meta: ActionMeta<MacroOption>) => {
    const { action } = meta;
    const { key, macro, isResourceMatch } = option;

    if (action !== 'select-option') return;

    const input = inputRef.current;

    if (key === 'show-all-results' || isResourceMatch) {
      input?.blur();
      callbacks.onSelect?.(option);
    }

    if (macro && input) {
      const strValue = createMacro(macro);
      if (input.getCurrentMacro()) {
        input.updateCurrentMacro(strValue);
        return;
      }

      const currentText = input.getCurrentText();
      if (currentText) {
        const value = (macro.value || currentText).trim();
        const newSegment = createMacro({ ...macro, value });
        input.replaceCurrentText(newSegment);
        return;
      }

      input.appendValue(strValue);
    }
  }, []);

  const [currentText, setCurrentText] = useState('');

  const selectStyles = useMemo(() => {
    if (!props.menuInPortal) return baseStyles;
    return {
      ...baseStyles,
      menuPortal: (base: any) => ({ ...base, zIndex: 1400 }),
    };
  }, [props.menuInPortal]);

  const options = useMemo(() => {
    const inputApi = inputRef.current;
    const resources = getResourceOptions(resourceSuggestions, getSourceMeta);

    // add an invisible option at the top which gets selected by default as the
    // user is typing. pressing Enter selects this option and thereby loads all
    // search results.
    let result = [{ key: 'show-all-results' }];

    if (inputApi && currentText) {
      result.push(...getStaticOptions({ inputApi, availableFilters }), ...resources);
    }

    result.push(...getDefaultOptions(availableFilters));

    return result;
  }, [currentText, lastUpdated]);

  return (
    <Select
      ref={selectRef}
      className="macro-autocomplete"
      autoFocus={false}
      value={null}
      menuIsOpen={props.isOpen}
      isMulti={false}
      isSearchable={true}
      openMenuOnFocus={false}
      closeMenuOnSelect={false}
      escapeClearsValue={false}
      isClearable={false}
      isLoading={isLoading}
      options={options}
      filterOption={() => true}
      styles={selectStyles}
      menuPortalTarget={props.menuInPortal ? document.body : undefined}
      menuPosition={props.menuInPortal ? 'fixed' : undefined}
      onInputChange={setCurrentText}
      onChange={onSelect}
      onFocus={state.setFocused}
      onBlur={state.clearFocused}
      onMenuOpen={state.setOpen}
      onMenuClose={state.clearOpen}
      components={customComponents}
    />
  );
};

export const MacroAutocomplete = forwardRef(MacroAutocompleteFn) as (
  props: MacroAutocompleteProps & { ref?: ForwardedRef<MacroInputApi> },
) => ReturnType<typeof MacroAutocompleteFn>;
